/**
 * PLUMBING STYLES — the 3D bodies (2026-10-07, Matt: "Spend the time to make them nice and try to
 * represent a broad swath of the quality and character that is being done by some of the best
 * and most respected designers out there"). One builder per style in the kernel catalog
 * (kernel categories/object/plumbingStyles.ts — names, real sizes, default finishes).
 *
 * Built at REAL SIZE in inches on the shared mesh kit (meshKit.ts): bowls, tanks, tub shells and
 * pedestals are LOFTS through stacks of superellipse / egg rings (the same 2D shapes the plan
 * symbols draw — `shapes` below), rims are BRIDGES between an outer and an inner loop, spouts,
 * arms, legs and hoses are TUBES along curves, handles and roses are LATHES.
 *
 * MATERIAL ROLES (each a solidGroup): 'metal' — the brushable trim (the instance's brushed colour
 * / project standard via el.memberColor, else the style's finish, over Plumbing Fixture Metal);
 * 'body' — the instance's Body Finish (china, stone, resin, cast iron) over its gloss; 'enamel'
 * (a tub's white interior, a basin set into a washstand), 'seat', 'porcelain' (handles, the chain
 * pull), 'wood' (the high-tank seat), 'water', 'dark' (drains, nozzle faces); clear glass goes to
 * the glass buffer.
 *
 * FRAMES (objectKinds.ts): x across centred on 0, y from the anchor (0 = the wall / the back) to
 * the front, z up. Counter-sitting styles build with the COUNTER at z = R (the rim height —
 * objectSolid drops the instance by R when the fixture sits on a real counter); wall styles build
 * with their mount point at z = M; floor styles from z = 0.
 */
import type { ParamValue, ResolvedElement } from '@aj/bim-kernel'
import {
  plumbingStyleOf, plumbingWidthIn, bodyFinishOf, plumbingBodyMaterialId, tintMaterialId,
  PLUMBING_METAL_MATERIAL_ID, WALL_MOUNT_DEFAULT_FT, type PlumbingStyle,
  WALL_HUNG, wallHungRimIn, wallHungRoughIn, wallHungZSpanIn,
} from '@aj/bim-kernel'
import { MeshBuilder } from '../../extrude'
import { LB, add, rotX, rotZ, type V3, type M3 } from '../../meshKit'
import { tubFillerPoint } from './freestandingTub'
import { grillBuiltInGas, grillHybridCart, grillKamadoTable, grillArgentine, grillSantaMaria, grillOffsetSmoker } from './grill3d'
import { featureEdges } from '../../edges'
import type { OpeningProto } from '../opening/protos'

type P2 = [number, number]
const rotY = (a: number): M3 => { const c = Math.cos(a), s = Math.sin(a); return [c, 0, s, 0, 1, 0, -s, 0, c] }
const FACE_OUT = rotX(-Math.PI / 2)          // a lathe's axis turned to +y (out of the wall)
const ALONG_X = rotY(Math.PI / 2)             // a lathe's axis turned to +x

// ======================================================================================
// 2D shapes (inches) — shared with the plan symbols
// ======================================================================================

const N = 56
/** a superellipse of half-sizes (a, b) centred (cx, cy): e = 2 an ellipse, higher squares it
 *  off. CCW from +x, n points. */
export function sel(a: number, b: number, e: number, cx = 0, cy = 0, n = N): P2[] {
  const out: P2[] = []
  for (let i = 0; i < n; i++) {
    const t = (Math.PI * 2 * i) / n, c = Math.cos(t), s = Math.sin(t)
    out.push([cx + a * Math.sign(c) * Math.abs(c) ** (2 / e), cy + b * Math.sign(s) * Math.abs(s) ** (2 / e)])
  }
  return out
}
/** an egg: a superellipse narrowing toward the FRONT (+y) by `taper` */
export function egg(a: number, b: number, cx: number, cy: number, taper: number, e = 2.2, n = N): P2[] {
  const out: P2[] = []
  for (let i = 0; i < n; i++) {
    const t = (Math.PI * 2 * i) / n, c = Math.cos(t), s = Math.sin(t)
    const k = 1 - taper * Math.max(0, s)
    out.push([cx + a * k * Math.sign(c) * Math.abs(c) ** (2 / e), cy + b * Math.sign(s) * Math.abs(s) ** (2 / e)])
  }
  return out
}
const ring = (pts: P2[], z: number): V3[] => pts.map((p) => [p[0], p[1], z])
const ringZ = (pts: P2[], zOf: (p: P2) => number): V3[] => pts.map((p) => [p[0], p[1], zOf(p)])

// ======================================================================================
// shared trim pieces
// ======================================================================================

/** a stepped round deck rosette, its top at c.z + h */
function rosette(L: LB, c: V3, r = 1.05, h = 0.5) {
  L.role('metal').latheAt(c, [[0, h], [r * 0.62, h], [r * 0.72, h * 0.8], [r * 0.9, h * 0.55], [r, h * 0.3], [r, 0], [0, 0]], 28)
}
/** a wall rosette (axis out of the wall at y = 0) */
function wallRosette(L: LB, c: V3, r = 1.2, t = 0.55) {
  L.role('metal').latheAt(c, [[0, t], [r * 0.55, t], [r * 0.75, t * 0.8], [r, t * 0.35], [r, 0], [0, 0]], 28, 0, FACE_OUT)
}
/** a four-arm cross handle with ball ends (the Edwardian handle), its hub base at c */
function crossHandle(L: LB, c: V3, s = 1, button = true) {
  L.role('metal').latheAt(c, [[0, 1.5 * s], [0.3 * s, 1.5 * s], [0.34 * s, 1.15 * s], [0.5 * s, 0.75 * s], [0.6 * s, 0.35 * s], [0.6 * s, 0], [0, 0]], 20)
  const z = c[2] + 1.18 * s
  for (let k = 0; k < 4; k++) {
    const a = (Math.PI / 2) * k + Math.PI / 4
    const tip: V3 = [c[0] + Math.cos(a) * 1.25 * s, c[1] + Math.sin(a) * 1.25 * s, z + 0.12 * s]
    L.role('metal').tube([[c[0], c[1], z], tip], 0.15 * s, 8)
    L.sphere(tip, 0.24 * s, 10)
  }
  if (button) L.role('porcelain').latheAt([c[0], c[1], c[2] + 1.5 * s], [[0, 0.22 * s], [0.25 * s, 0.12 * s], [0.3 * s, 0], [0, 0]], 14)
}
/** a metal lever on its hub, the blade pointing along `dir` (radians in plan) */
function leverHandle(L: LB, c: V3, dir: number, len = 3.3, porcelain = false) {
  L.role('metal').latheAt(c, [[0, 1.3], [0.42, 1.3], [0.46, 0.9], [0.62, 0.4], [0.62, 0], [0, 0]], 20)
  const z = c[2] + 1.05
  const tip: V3 = [c[0] + Math.cos(dir) * len, c[1] + Math.sin(dir) * len, z + 0.5]
  const mid: V3 = [c[0] + Math.cos(dir) * len * 0.45, c[1] + Math.sin(dir) * len * 0.45, z + 0.12]
  L.role(porcelain ? 'porcelain' : 'metal').tube([[c[0], c[1], z], mid, tip], porcelain ? 0.24 : 0.17, 10)
  L.sphere(tip, porcelain ? 0.3 : 0.22, 10)
}
/** a knurled cylinder handle on a hex nut */
function knurledHandle(L: LB, c: V3) {
  L.role('metal').latheAt(c, [[0, 0.55], [0.75, 0.55], [0.75, 0], [0, 0]], 6, Math.PI / 6)
  const prof: [number, number][] = [[0, 2.05], [0.5, 2.05], [0.62, 1.95]]
  for (let i = 0; i < 7; i++) { const z = 1.9 - i * 0.2; prof.push([0.66, z], [0.6, z - 0.1]) }
  prof.push([0.62, 0.55], [0.35, 0.55], [0, 0.55])
  L.latheAt(c, prof, 24)
}
/** a low C-arc spout from its deck collar at c: rises then arcs forward to the outlet */
function arcSpout(L: LB, c: V3, rise: number, reach: number, outletZ: number, r = 0.42) {
  rosette(L, c, 1.0, 0.45)
  L.role('metal').latheAt(add(c, [0, 0, 0.45]), [[0, 1.1], [0.55, 1.1], [0.6, 0.6], [0.72, 0], [0, 0]], 20)
  const p0: V3 = add(c, [0, 0, 1.4])
  const o: V3 = [c[0], c[1] + reach, outletZ]
  const path = LB.bez(p0, add(c, [0, 0, rise + 0.8]), add(c, [0, reach * 0.7, rise + 1.0]), add(o, [0, 0, 0.5]), 18)
  L.tube(path, r, 14)
  L.cyl(add(o, [0, 0, 0.6]), add(o, [0, 0, -0.2]), r * 1.05, 14)   // aerator
}
/** a tall gooseneck from c: up the column, over, and down to the outlet */
function gooseneck(L: LB, c: V3, height: number, reach: number, outletZ: number, r = 0.45) {
  const o: V3 = [c[0], c[1] + reach, outletZ]
  const path = LB.bez(c, add(c, [0, 0, height * 1.25]), add(c, [0, reach * 1.05, height * 1.2]), o, 26)
  L.role('metal').tube(path, r, 14)
  L.cyl(add(o, [0, 0, 0.2]), add(o, [0, 0, -0.7]), r * 1.12, 14)
}
/** a telephone hand-shower handset lying along `dir` from c (handle, then the rose cup) */
function handset(L: LB, c: V3, dir: V3, len = 7.5) {
  const end = add(c, [dir[0] * len, dir[1] * len, dir[2] * len])
  L.role('metal').tube([c, end], 0.42, 12)
  L.sphere(c, 0.5, 10)
  L.latheAt(end, [[0, 0.9], [0.55, 0.9], [1.5, 0.2], [1.6, -0.3], [0, -0.3]], 20)
  L.role('dark').latheAt(add(end, [0, 0, -0.32]), [[0, 0.02], [1.45, 0.02], [1.45, 0], [0, 0]], 18)
}
/** a drain flange (dark throat) at c */
function drain(L: LB, c: V3, r = 0.85) {
  L.role('metal').latheAt(c, [[0, 0.12], [r, 0.08], [r, 0], [0, 0]], 20)
  L.role('dark').latheAt(add(c, [0, 0, 0.13]), [[0, 0.01], [r * 0.6, 0.01], [r * 0.6, 0], [0, 0]], 14)
}

// ======================================================================================
// TOILETS (floor z = 0, wall y = 0)
// ======================================================================================

/** the seat and the closed lid on a bowl rim (egg a×b centred cy at rimZ) */
function seatAndLid(L: LB, a: number, b: number, cy: number, taper: number, rimZ: number, role = 'seat') {
  const o = (k: number, z: number) => ring(egg(a * k, b * k, 0, cy, taper), z)
  L.role(role)
  L.loft([o(0.99, rimZ), o(1.0, rimZ + 0.35), o(1.0, rimZ + 0.75)])
  L.loft([o(0.985, rimZ + 0.78), o(0.985, rimZ + 1.15), o(0.95, rimZ + 1.45), o(0.88, rimZ + 1.6)])
  L.bridge(o(1.0, rimZ + 0.75), o(0.985, rimZ + 0.78))
  L.capLoop(o(0.88, rimZ + 1.6), true)
  L.capLoop(o(0.99, rimZ), false)
  L.role('metal')
  for (const x of [-2.8, 2.8]) L.cyl([x, cy - b + 0.3, rimZ + 0.6], [x, cy - b + 1.4, rimZ + 0.6], 0.45, 12)
}
/** the bowl: a pedestal flaring from its foot up to the rim, the china rim, the inside */
function toiletBowl(L: LB, o: { a: number; b: number; cy: number; taper: number; rimZ: number; footA: number; footB: number; footCy: number; footZ?: number; skirt?: boolean }) {
  const { a, b, cy, taper, rimZ } = o
  const fz = o.footZ ?? 0
  const e = (k: number, kb: number, c: number, z: number, t = taper, ex = 2.2) => ring(egg(a * k, b * kb, 0, c, t, ex), z)
  L.role('body')
  if (o.skirt) {
    // the one-piece skirt: near-straight sides to the floor
    L.loft([e(0.97, 1.02, cy - 1.4, 0, taper * 0.6, 3), e(0.98, 1.02, cy - 1.4, 0.5, taper * 0.6, 3), e(1.0, 1.0, cy - 0.8, rimZ - 3, taper * 0.8, 2.6), e(1.0, 1.0, cy, rimZ - 0.6), e(0.98, 0.98, cy, rimZ)], [1])
    L.capLoop(e(0.97, 1.02, cy - 1.4, 0, taper * 0.6, 3), false)
  } else {
    const foot = (k: number, z: number) => ring(egg(o.footA * k, o.footB * k, 0, o.footCy, 0.1, 2.6), z)
    L.loft([foot(1, fz), foot(1, fz + 0.6), foot(0.86, fz + 3.5), ring(egg(a * 0.78, b * 0.9, 0, cy - 0.6, taper, 2.3), rimZ - 6.5), e(0.95, 0.98, cy, rimZ - 2.5), e(1.0, 1.0, cy, rimZ - 0.5), e(0.985, 0.99, cy, rimZ)], [1])
    L.capLoop(foot(1, fz), false)
  }
  // the rim top, then the bowl's inside down to the water
  const inner = (k: number, z: number) => ring(egg(a * 0.8 * k, b * 0.82 * k, 0, cy + 0.4, taper * 0.8), z)
  L.bridge(e(0.985, 0.99, cy, rimZ), inner(1, rimZ))
  L.loft([inner(1, rimZ), inner(0.9, rimZ - 2.2), inner(0.66, rimZ - 4.4), inner(0.42, rimZ - 5.6)])
  L.role('water').capLoop(inner(0.42, rimZ - 5.6), true)
}
/** a close-coupled / high-tank cistern: a softened box, its lid with an overhang */
function cistern(L: LB, w: number, d: number, y0: number, z0: number, z1: number, role = 'body') {
  const a = w / 2, b = d / 2, cy = y0 + b
  const s = (k: number, z: number, ex = 6) => ring(sel(a * k, b * k, ex, 0, cy), z)
  L.role(role)
  L.loft([s(0.97, z0), s(0.985, z0 + 0.8), s(1.0, z1 - 1.0)], [])
  L.capLoop(s(0.97, z0), false)
  // the lid: a slight overhang with an eased top edge
  L.loft([s(1.0, z1 - 1.0), s(1.025, z1 - 0.95), s(1.025, z1 - 0.3), s(1.0, z1)], [1])
  L.capLoop(s(1.0, z1), true)
}
function toiletCloseCoupled(L: LB) {
  const rimZ = 15.6, a = 7.25, b = 9.6, cy = 30.4 - b
  toiletBowl(L, { a, b, cy, taper: 0.16, rimZ, footA: 5.0, footB: 8.4, footCy: 18.6 })
  // the neck to the cistern
  L.role('body').loft([ring(sel(5.4, 3.4, 3.2, 0, 10.6), 6), ring(sel(6.4, 3.6, 3.2, 0, 10.6), 16.2)])
  L.capLoop(ring(sel(5.4, 3.4, 3.2, 0, 10.6), 6), false)
  cistern(L, 18.9, 8.95, 0.5, 16.2, 31.1)
  // the trip lever on the cistern's front, left
  wallRosetteAt(L, [-6.6, 9.45, 27.6], 0.75, 0.35)
  L.role('metal').tube([[-6.6, 9.8, 27.6], [-5.4, 10.2, 27.75], [-3.4, 10.25, 27.9]], 0.2, 10)
  L.sphere([-3.4, 10.25, 27.9], 0.26, 10)
  seatAndLid(L, a, b, cy, 0.16, rimZ)
}
/** a small rosette facing +y at an arbitrary point (a lever escutcheon) */
function wallRosetteAt(L: LB, c: V3, r: number, t: number) {
  L.role('metal').latheAt(c, [[0, t], [r * 0.6, t], [r, t * 0.4], [r, 0], [0, 0]], 20, 0, FACE_OUT)
}
function toiletHighTank(L: LB) {
  const rimZ = 15.4, a = 7.2, b = 8.9, cy = 27.5 - b
  toiletBowl(L, { a, b, cy, taper: 0.1, rimZ, footA: 4.9, footB: 7.8, footCy: 16.2 })
  L.role('body').loft([ring(sel(3.6, 2.2, 3, 0, 9.8), 8), ring(sel(4.2, 2.6, 3, 0, 10.2), rimZ - 0.2)])
  L.capLoop(ring(sel(4.2, 2.6, 3, 0, 10.2), rimZ - 0.2), true)
  // the cistern up on its two cast brackets
  cistern(L, 20, 8.6, 0.9, 70.5, 84)
  L.role('metal')
  for (const x of [-7, 7]) {
    wallRosetteAt(L, [x, 0, 67.5], 1.0, 0.4)
    L.tube(LB.bez([x, 0.3, 67.5], [x, 4, 67.6], [x, 6, 69], [x, 6.4, 70.5], 10), 0.32, 4)
  }
  // the exposed flush pipe, down the wall and into the bowl's back
  L.tube([[0, 5.2, 70.5], [0, 5.2, 24], ...LB.bez([0, 5.2, 24], [0, 5.2, 19], [0, 7, 17.5], [0, 10.4, 17.2], 10).slice(1)], 0.72, 16)
  L.latheAt([0, 5.2, 69.6], [[0, 0.9], [1.0, 0.9], [1.0, 0], [0, 0]], 18)
  // the pull: lever arm, chain, porcelain handle
  L.tube([[6.6, 9.4, 81], [8.6, 10.8, 81.2]], 0.2, 8)
  L.chain([8.6, 10.8, 80.6], [8.6, 10.8, 58.5], 0.95, 0.55, 0.09)
  L.role('porcelain').latheAt([8.6, 10.8, 54.0], [[0, 4.4], [0.3, 4.4], [0.45, 3.6], [0.7, 1.4], [0.6, 0.4], [0, 0]], 16)
  seatAndLid(L, a, b, cy, 0.1, rimZ, 'wood')
}
function toiletSkirted(L: LB) {
  const rimZ = 15.6, a = 7.2, b = 9.4, cy = 28.5 - b
  toiletBowl(L, { a, b, cy, taper: 0.14, rimZ, footA: 0, footB: 0, footCy: 0, skirt: true })
  // the low integrated cistern: a soft block over the back
  const s = (k: number, z: number) => ring(sel(8.9 * k, 4.6 * k, 4.2, 0, 5.4), z)
  L.role('body').loft([s(0.94, 6), s(1.0, 12), s(1.0, 21.6), s(0.96, 22.4), s(0.85, 22.6)])
  L.capLoop(s(0.85, 22.6), true)
  L.capLoop(s(0.94, 6), false)
  L.role('metal').latheAt([0, 4.2, 22.6], [[0, 0.18], [0.9, 0.18], [0.95, 0], [0, 0]], 20)   // the flush button
  seatAndLid(L, a, b, cy, 0.14, rimZ)
}
function toiletWallHung(L: LB) {
  const rimZ = 15.8, a = 7.3, b = 10.6, cy = 21.3 - b
  const e = (ka: number, kb: number, c: number, z: number) => ring(egg(a * ka, b * kb, 0, c, 0.12, 2.6), z)
  L.role('body')
  // the floating bowl: a soft underside rising into the wall
  L.loft([e(0.55, 0.62, cy - 2.5, 7.4), e(0.78, 0.82, cy - 1.4, 8.6), e(0.95, 0.96, cy - 0.4, 11.4), e(1.0, 1.0, cy, rimZ - 0.6), e(0.985, 0.99, cy, rimZ)], [])
  L.capLoop(e(0.55, 0.62, cy - 2.5, 7.4), false)
  const sh = (k: number, z: number) => ring(sel(5.6 * k, 3.2, 4, 0, 3.2), z)
  L.loft([sh(0.8, 9.0), sh(1.0, 11.5), sh(1.0, rimZ - 1.2)])
  L.capLoop(sh(0.8, 9.0), false)
  L.capLoop(sh(1.0, rimZ - 1.2), true)
  const inner = (k: number, z: number) => ring(egg(a * 0.8 * k, b * 0.8 * k, 0, cy + 0.6, 0.1), z)
  L.bridge(e(0.985, 0.99, cy, rimZ), inner(1, rimZ))
  L.loft([inner(1, rimZ), inner(0.88, rimZ - 2.4), inner(0.62, rimZ - 4.6), inner(0.4, rimZ - 5.6)])
  L.role('water').capLoop(inner(0.4, rimZ - 5.6), true)
  seatAndLid(L, a, b, cy, 0.12, rimZ)
  // the flush plate on the wall over the carrier, with its two buttons
  L.role('metal').box(-4.8, 4.8, 0, 0.35, 41.1, 47.6)
  L.role('dark').box(-4.6, 4.6, 0.35, 0.37, 41.3, 47.4)
  L.role('metal').box(-4.3, -0.15, 0.37, 0.55, 41.6, 47.1)
  L.box(0.15, 4.3, 0.37, 0.55, 41.6, 47.1)
}

// ======================================================================================
// BATH BASINS
// ======================================================================================

/** an undermount bowl hanging below the counter at R: its inside (seen through the counter's
 *  cut-out), its outside skin, the drain. top = the cut-out loop. */
function undermountBowl(L: LB, top: (k: number) => P2[], depth: number, R: number, role = 'body', bottomK = 0.55, lip = 1.12) {
  const r = (k: number, z: number) => ring(top(k), z)
  L.role(role)
  L.loft([r(1, R), r(0.985, R - depth * 0.35), r(0.9, R - depth * 0.75), r(bottomK + 0.12, R - depth * 0.95), r(bottomK, R - depth)])
  L.capLoop(r(bottomK, R - depth), true)
  // the outside (for sections), a lip under the counter
  L.loft([r(bottomK * 1.05, R - depth - 0.45), r(0.93, R - depth * 0.75), r(1 + (lip - 1) / 2, R - 0.5), r(lip, R - 0.2)])
  L.bridge(r(lip, R - 0.2), r(1, R), true)
  L.capLoop(r(bottomK * 1.05, R - depth - 0.45), false)
  const c = top(0)[0]
  drain(L, [c[0], c[1], R - depth + 0.01])
}
function basinUndermountRect(L: LB, R: number) {
  undermountBowl(L, (k) => sel(8.6 * k, 6.5 * k, 5.5, 0, 7.8), 6.4, R, 'body', 0.62)
}
function basinUndermountOval(L: LB, R: number) {
  undermountBowl(L, (k) => sel(8.5 * k, 7.0 * k, 2.1, 0, 8.1), 6.8, R, 'body', 0.42)
}
function basinVesselRound(L: LB, R: number) {
  // the carved bowl: outside rounding to a small foot, a flat lip, a smooth inside
  L.role('body').latheAt([0, 9, R], [[8.35, 6.0], [9.0, 6.0], [9.0, 5.6], [8.7, 3.8], [7.8, 1.8], [6.6, 0.45], [6.0, 0], [0, 0]], 48)
  L.latheAt([0, 9, R], [[0, 0.95], [3.2, 1.05], [5.8, 1.8], [7.5, 3.4], [8.3, 5.2], [8.35, 6.0]], 48)
  drain(L, [0, 9, R + 0.96], 0.8)
}
function basinVesselBlock(L: LB, R: number) {
  const o = (z: number, k = 1) => ring(sel(12 * k, 7.5 * k, 12, 0, 7.5), z)
  const i = (z: number, k: number) => ring(sel(10 * k, 5.6 * k, 4, 0, 7.6), z)
  L.role('body').loft([o(R), o(R + 4.7), o(R + 5, 0.995)], [1])
  L.capLoop(o(R), false)
  L.bridge(o(R + 5, 0.995), i(R + 5, 1))
  L.loft([i(R + 5, 1), i(R + 3.2, 0.97), i(R + 1.5, 0.88), i(R + 1.2, 0.84)])
  L.capLoop(i(R + 1.2, 0.84), true)
  drain(L, [0, 7.6, R + 1.21], 0.75)
}
/** a marble washstand on turned metal legs: the top with its undermount bowl, the splash, the
 *  legs, the towel rail and the low frame */
function basinWashstand(L: LB, W: number) {
  const a = W / 2, zt = 34.5, zb = 33
  const top = (z: number, k = 1) => ring(sel(a * k, 11 * k, 14, 0, 11), z)
  const bowl = (k: number) => sel(Math.min(9, a - 3.5) * k, 6.6 * k, 5, 0, 12.2)
  L.role('body').loft([top(zb), top(zt - 0.15), top(zt, 0.997)], [1])
  L.capLoop(top(zb), false)
  L.bridge(top(zt, 0.997), ring(bowl(1), zt))
  undermountBowl(L, bowl, 6.0, zt, 'enamel', 0.6)
  L.role('body').box(-a, a, 0, 0.75, zt, zt + 4)                                   // backsplash
  // the four turned legs
  const leg: [number, number][] = [[0, zb], [0.62, zb], [0.62, zb - 0.6], [0.48, zb - 1.0], [0.5, zb - 3.5], [0.62, zb - 4], [0.5, zb - 4.6],
    [0.42, 10], [0.5, 8.6], [0.62, 8.2], [0.5, 7.6], [0.42, 6], [0.48, 3.0], [0.72, 1.6], [0.72, 0.6], [0.55, 0], [0, 0]]
  const xs = [-(a - 2.0), a - 2.0], ys = [2.4, 19.6]
  for (const x of xs) for (const y of ys) L.role('metal').latheAt([x, y, 0], leg, 18)
  // the low frame between the legs, the towel rail along the front
  for (const y of ys) L.tube([[xs[0], y, 8.2], [xs[1], y, 8.2]], 0.32, 10, false)
  for (const x of xs) L.tube([[x, ys[0], 8.2], [x, ys[1], 8.2]], 0.32, 10, false)
  L.tube([[xs[0], ys[1], 29], [xs[0], ys[1] + 1.6, 29], [xs[1], ys[1] + 1.6, 29], [xs[1], ys[1], 29]], 0.34, 12)
}
function basinPedestal(L: LB) {
  // the fluted column on its foot
  const fl = (a: number, b: number, z: number, depth = 0.08) => {
    const pts = sel(a, b, 2, 0, 8.2, 96).map((p, i) => {
      const t = (Math.PI * 2 * i) / 96, k = 1 - depth * Math.abs(Math.sin(t * 8))
      return [p[0] * k, 8.2 + (p[1] - 8.2) * k] as P2
    })
    return ring(pts, z)
  }
  L.role('body').loft([fl(5.4, 4.6, 0, 0), fl(5.4, 4.6, 0.8, 0), fl(4.4, 3.8, 1.6, 0.02), fl(3.5, 3.0, 5), fl(3.3, 2.8, 20), fl(3.7, 3.2, 24.5), fl(4.4, 3.7, 26.2, 0.02)], [1])
  L.capLoop(fl(5.4, 4.6, 0, 0), false)
  // the basin: a rolled front rim, a deck behind, the oval bowl
  const o = (a: number, b: number, z: number, cy = 9.95) => ring(sel(a, b, 3.2, 0, cy), z)
  L.loft([o(8.0, 6.4, 25.8, 10.6), o(10.6, 8.7, 28, 10.1), o(11.9, 9.8, 31.4), o(12.1, 9.95, 33.6), o(11.8, 9.7, 34.4)])
  L.capLoop(o(8.0, 6.4, 25.8, 10.6), false)
  const bw = (k: number, z: number) => ring(sel(7.5 * k, 6.5 * k, 2.1, 0, 11.4), z)
  L.bridge(o(11.8, 9.7, 34.4), bw(1, 34.4))
  L.loft([bw(1, 34.4), bw(0.92, 32.4), bw(0.7, 30.2), bw(0.45, 29.3)])
  L.capLoop(bw(0.45, 29.3), true)
  drain(L, [0, 11.4, 29.31], 0.75)
}
function basinTrough(L: LB, W: number) {
  const a = W / 2, z0 = 27.5, z1 = 34
  const o = (z: number, k = 1) => ring(sel(a * k, 9.5, 14, 0, 9.5), z)
  const t = (k: number, z: number) => ring(sel((a - 2) * k, 6 * k, 6, 0, 10.6), z)
  L.role('body').loft([o(z0), o(z1 - 0.2), o(z1, 0.998)], [1])
  L.capLoop(o(z0), false)
  L.bridge(o(z1, 0.998), t(1, z1))
  L.loft([t(1, z1), t(0.995, z1 - 2.6), t(0.97, z1 - 3.8), t(0.94, z1 - 4.1)])
  L.capLoop(t(0.94, z1 - 4.1), true)
  L.role('dark').box(-(a - 5), a - 5, 6.0, 6.8, z1 - 4.09, z1 - 4.06)               // the slot drain
}

// ======================================================================================
// KITCHEN SINKS (counter at R)
// ======================================================================================

function apronSink(L: LB, W: number, R: number, opts: { fluted?: boolean; stone?: boolean; d?: number }) {
  const d = opts.d ?? 18, a = W / 2, b = d / 2, cy = b
  const o = (z: number, k = 1, e = 12) => ring(sel(a * k, b * k, e, 0, cy), z)
  const wall = opts.stone ? 1.9 : 1.5
  const bw = (k: number, z: number, e = 7) => ring(sel((a - wall) * k, (b - wall) * k, e, 0, cy + 0.3), z)
  L.role('body')
  L.loft([o(R - 10, 0.99), o(R - 9.7), o(R - 0.35), o(R, 0.995)], [1, 2])
  L.capLoop(o(R - 10, 0.99), false)
  L.bridge(o(R, 0.995), bw(1, R))
  L.loft([bw(1, R), bw(0.995, R - 1.2), bw(0.985, R - 6.5), bw(0.95, R - 8.2), bw(0.9, R - 8.5, 6)])
  L.capLoop(bw(0.9, R - 8.5, 6), true)
  drain(L, [0, cy + 0.3, R - 8.49], 1.75)
  if (opts.fluted) {
    // the fluted apron: half-round reeds across the front face
    const n = Math.max(6, Math.floor((W - 3) / 1.25))
    for (let i = 0; i < n; i++) {
      const x = -a + 1.5 + (i * (W - 3)) / (n - 1)
      L.role('body').cyl([x, d - 0.12, R - 9.4], [x, d - 0.12, R - 0.6], 0.42, 10)
    }
  }
}
function sinkUndermountSteel(L: LB, W: number, R: number) {
  undermountBowl(L, (k) => sel((W / 2 - 0.6) * k, 8.6 * k, 9, 0, 9), 9.5, R, 'metal', 0.92, 1.035)
}
function sinkPrepStone(L: LB, W: number, R: number) {
  undermountBowl(L, (k) => sel((W / 2 / 1.1) * k, 7 * k, 6, 0, 7.5), 7.2, R, 'body', 0.82, 1.1)
}

// ======================================================================================
// LAVATORY FAUCETS (deck at z = R, y = 0 the hole line)
// ======================================================================================

function lavWidespread(L: LB, R: number, handle: 'cross' | 'lever' | 'porcelain') {
  arcSpout(L, [0, 0, R], 5.0, 5.4, R + 3.2)
  for (const s of [-1, 1]) {
    rosette(L, [s * 4, 0, R], 1.0, 0.45)
    if (handle === 'cross') crossHandle(L, [s * 4, 0, R + 0.45], 1)
    else leverHandle(L, [s * 4, 0, R + 0.45], s < 0 ? Math.PI * 0.62 : Math.PI * 0.38, 3.3, handle === 'porcelain')
  }
}
function lavBridge(L: LB, R: number) {
  // two posts, the bridge across, the swan spout from its centre, cross handles on the posts
  for (const s of [-1, 1]) {
    rosette(L, [s * 4, 0, R], 1.05, 0.5)
    L.role('metal').latheAt([s * 4, 0, R + 0.5], [[0, 3.2], [0.62, 3.2], [0.62, 2.7], [0.5, 2.4], [0.46, 0.6], [0.62, 0.3], [0.62, 0], [0, 0]], 20)
    crossHandle(L, [s * 4, 0, R + 3.7], 0.95)
  }
  L.role('metal').tube([[-4, 0, R + 2.9], [4, 0, R + 2.9]], 0.42, 14)
  L.latheAt([0, 0, R + 2.4], [[0, 1.4], [0.6, 1.4], [0.75, 0.6], [0.75, 0], [0, 0]], 20)
  gooseneck(L, [0, 0, R + 3.6], 5.5, 6.3, R + 4.9, 0.4)
}
function lavGooseneckLevers(L: LB, R: number) {
  rosette(L, [0, 0, R], 1.25, 0.5)
  L.role('metal').latheAt([0, 0, R + 0.5], [[0, 5.0], [0.7, 5.0], [0.78, 4.5], [0.7, 3.6], [0.66, 1.2], [0.8, 0.4], [0.8, 0], [0, 0]], 22)
  for (const s of [-1, 1]) {
    L.role('metal').cyl([0, 0, R + 3.0], [s * 1.9, 0, R + 3.0], 0.36, 12)
    leverHandle(L, [s * 2.1, 0, R + 2.6], s < 0 ? Math.PI * 0.86 : Math.PI * 0.14, 2.6)
  }
  gooseneck(L, [0, 0, R + 5.4], 6.9, 6.0, R + 6.3, 0.38)
}
function lavIndustrial(L: LB, R: number) {
  // a straight riser bent to a horizontal tube, a 90° drop to the outlet; knurled handles on hex nuts
  rosette(L, [0, 0, R], 1.0, 0.45)
  L.role('metal').latheAt([0, 0, R + 0.45], [[0, 0.7], [0.72, 0.7], [0.72, 0], [0, 0]], 6, Math.PI / 6)
  L.role('metal').tube([[0, 0, R + 1.1], [0, 0, R + 6.2], [0, 0.6, R + 6.8], [0, 5.4, R + 6.8], [0, 5.9, R + 6.3], [0, 5.9, R + 5.2]], 0.42, 14)
  for (const s of [-1, 1]) { rosette(L, [s * 4, 0, R], 1.0, 0.45); knurledHandle(L, [s * 4, 0, R + 0.45]) }
}
function lavMinimal(L: LB, R: number, tall = false) {
  const h = tall ? 12.0 : 6.6, reach = tall ? 6.3 : 5.4
  L.role('metal').latheAt([0, 0, R], [[0, h], [0.95, h], [1.05, h - 0.15], [1.05, 0.3], [1.25, 0.15], [1.25, 0], [0, 0]], 28)
  L.role('metal').tube([[0, 0.6, R + h - 1.2], [0, reach, R + h - 1.2]], 0.36, 14)
  L.cyl([0, reach, R + h - 1.2], [0, reach, R + h - 1.8], 0.38, 14)
  // the disc-and-pin handle on top (or a side lever on the tall one)
  if (tall) leverHandle(L, [1.1, 0, R + h - 2.4], 0, 2.4)
  else { L.latheAt([0, 0, R + h], [[0, 0.5], [0.95, 0.5], [0.95, 0], [0, 0]], 28); L.cyl([0, 0, R + h + 0.5], [0, 0, R + h + 0.6], 0.2, 8); L.tube([[0, 0, R + h + 0.6], [0, -2.2, R + h + 0.6]], 0.16, 8) }
}
function lavWall(L: LB, R: number, cross: boolean) {
  const z = R + (cross ? 8.5 : 8.0)
  wallRosette(L, [0, 0, z], 1.25, 0.6)
  L.role('metal').tube([[0, 0.5, z], [0, cross ? 8.0 : 7.6, z - 0.3]], 0.42, 14)
  L.cyl([0, cross ? 8.0 : 7.6, z - 0.1], [0, cross ? 8.0 : 7.6, z - 0.9], 0.46, 14)
  if (cross) {
    for (const s of [-1, 1]) {
      wallRosette(L, [s * 4, 0, z + 1.2], 1.0, 0.5)
      L.role('metal').tube([[s * 4, 0.5, z + 1.2], [s * 4, 1.6, z + 1.2]], 0.4, 12)
      L.frame([s * 4, 1.6, z + 1.2], FACE_OUT, () => crossHandle(L, [0, 0, 0], 0.9))
    }
  } else {
    wallRosette(L, [3.4, 0, z + 1.4], 0.9, 0.45)
    L.role('metal').tube([[3.4, 0.4, z + 1.4], [3.4, 1.2, z + 1.4]], 0.34, 12)
    L.tube([[3.4, 1.2, z + 1.4], [3.4, 1.4, z + 4.0]], 0.15, 8)
  }
}

// ======================================================================================
// KITCHEN FAUCETS (deck at z = R)
// ======================================================================================

function kBridge(L: LB, R: number, lever: boolean, spray: boolean) {
  for (const s of [-1, 1]) {
    rosette(L, [s * 4, 0, R], 1.2, 0.55)
    L.role('metal').latheAt([s * 4, 0, R + 0.55], [[0, 4.0], [0.66, 4.0], [0.66, 3.4], [0.54, 3.0], [0.5, 0.7], [0.7, 0.3], [0.7, 0], [0, 0]], 20)
    if (lever) leverHandle(L, [s * 4, 0, R + 4.55], s < 0 ? Math.PI * 0.62 : Math.PI * 0.38, 3.6)
    else crossHandle(L, [s * 4, 0, R + 4.55], 1.1)
  }
  L.role('metal').tube([[-4, 0, R + 3.6], [4, 0, R + 3.6]], 0.5, 14)
  L.latheAt([0, 0, R + 3.0], [[0, 1.8], [0.7, 1.8], [0.85, 0.7], [0.85, 0], [0, 0]], 20)
  gooseneck(L, [0, 0, R + 4.6], 11.25, 8.9, R + 10.9, 0.52)
  if (spray) {
    // the side spray in its own deck holder
    rosette(L, [9.5, 0, R], 1.15, 0.5)
    L.role('metal').latheAt([9.5, 0, R + 0.5], [[0, 1.0], [0.85, 1.0], [0.85, 0], [0, 0]], 20)
    L.latheAt([9.5, 0, R + 1.5], [[0, 4.0], [0.6, 4.0], [0.72, 3.4], [0.62, 2.6], [0.55, 0.6], [0.7, 0], [0, 0]], 18)
  }
}
function kDeckMixer(L: LB, R: number) {
  for (const s of [-1, 1]) {
    rosette(L, [s * 5, 0, R], 1.1, 0.5)
    L.role('metal').latheAt([s * 5, 0, R + 0.5], [[0, 2.4], [0.6, 2.4], [0.6, 0], [0, 0]], 18)
    crossHandle(L, [s * 5, 0, R + 2.9], 1.1)
  }
  rosette(L, [0, 0, R], 1.25, 0.55)
  L.role('metal').latheAt([0, 0, R + 0.55], [[0, 2.2], [0.68, 2.2], [0.68, 0], [0, 0]], 20)
  gooseneck(L, [0, 0, R + 2.7], 12.2, 8.6, R + 9.4, 0.5)
}
function kWallBridge(L: LB, R: number) {
  const z = R + 12
  for (const s of [-1, 1]) {
    wallRosette(L, [s * 4, 0, z], 1.2, 0.6)
    L.role('metal').tube([[s * 4, 0.5, z], [s * 4, 2.6, z]], 0.5, 14)
    crossHandle(L, [s * 4, 2.6, z + 0.5], 1.1)
  }
  L.role('metal').tube([[-4, 2.6, z], [4, 2.6, z]], 0.55, 14)
  L.latheAt([0, 2.6, z - 1.0], [[0, 1.0], [0.8, 1.0], [0.8, 0], [0, 0]], 20)
  L.tube(LB.bez([0, 2.6, z - 1.0], [0, 2.6, z - 3.0], [0, 6.0, z - 3.4], [0, 12.4, z - 3.6], 14), 0.48, 14)
  L.cyl([0, 12.4, z - 3.4], [0, 12.4, z - 4.4], 0.52, 14)
}
function kGooseneck(L: LB, R: number) {
  rosette(L, [0, 0, R], 1.3, 0.55)
  L.role('metal').latheAt([0, 0, R + 0.55], [[0, 6.5], [0.95, 6.5], [1.05, 6.1], [1.0, 1.0], [1.15, 0.3], [1.15, 0], [0, 0]], 24)
  gooseneck(L, [0, 0, R + 7.0], 10.25, 10.3, R + 7.6, 0.55)
  leverHandle(L, [1.0, 0, R + 4.2], 0, 3.0)
}
function kPreRinse(L: LB, R: number) {
  rosette(L, [0, 0, R], 1.4, 0.6)
  L.role('metal').latheAt([0, 0, R + 0.6], [[0, 4.6], [1.0, 4.6], [1.0, 0], [0, 0]], 22)
  leverHandle(L, [1.0, 0, R + 2.6], 0, 2.6)
  L.role('metal').tube([[0, 0, R + 5.2], [0, 0, R + 21.4]], 0.3, 10)
  // the spring coil round the hose
  const coil: V3[] = []
  for (let i = 0; i <= 260; i++) { const t = i / 260, ang = t * Math.PI * 2 * 26; coil.push([Math.cos(ang) * 1.15, Math.sin(ang) * 1.15, R + 9 + t * 11]) }
  L.tube(coil, 0.1, 5, false)
  // the swing arm with its small spout, the spray head on the hose
  L.tube([[0, 0, R + 9.2], [0, 6.0, R + 9.2], [0, 6.6, R + 8.6], [0, 6.6, R + 7.8]], 0.4, 12)
  L.latheAt([0, 0, R + 20.5], [[0, 1.6], [0.5, 1.6], [0.6, 0.8], [0, 0]], 14)
  L.tube(LB.bez([0, 0, R + 21.4], [0, 0, R + 22], [0, 5, R + 22], [0, 9.6, R + 14], 16), 0.3, 10)
  L.latheAt([0, 9.6, R + 12], [[0, 2.2], [0.85, 2.2], [1.0, 0.6], [0.9, 0], [0, 0]], 18)
}

// ======================================================================================
// POT FILLERS (wall at y = 0, mount at z = M) — shown folded flat to the wall
// ======================================================================================

function potFiller(L: LB, M: number, cross: boolean) {
  wallRosette(L, [0, 0, M], 1.4, 0.7)
  L.role('metal').tube([[0, 0.6, M], [0, 2.4, M]], 0.48, 14)
  if (cross) crossHandle(L, [0, 2.4, M + 0.5], 1.0)
  // the knuckle joints and the two arms folded side by side along the wall
  const jt = (c: V3) => L.role('metal').latheAt(add(c, [0, 0, -0.9]), [[0, 1.8], [0.75, 1.8], [0.9, 1.4], [0.9, 0.4], [0.75, 0], [0, 0]], 18)
  jt([0, 2.4, M])
  L.role('metal').tube([[0, 2.4, M - 0.2], [8.0, 2.4, M - 0.2]], 0.45, 14)
  jt([8.0, 2.4, M - 0.2])
  L.role('metal').tube([[8.0, 3.7, M - 0.4], [-7.6, 3.7, M - 0.4]], 0.45, 14)
  L.tube([[8.0, 2.4, M - 0.4], [8.0, 3.7, M - 0.4]], 0.45, 14)
  jt([-7.6, 3.7, M - 0.4])
  if (cross) crossHandle(L, [-7.6, 3.7, M + 0.5], 0.8)
  L.role('metal').cyl([-7.6, 3.7, M - 1.2], [-7.6, 3.7, M - 2.0], 0.5, 14)
}

// ======================================================================================
// TUB FILLERS (floor: z = 0 the floor; wall: on the wall over a tub; rim: z = R the rim)
// ======================================================================================

function fillerFloorTwin(L: LB) {
  for (const s of [-1, 1]) {
    L.role('metal').latheAt([s * 1.6, 0, 0], [[0, 1.0], [0.9, 1.0], [1.4, 0.4], [1.6, 0.15], [1.6, 0], [0, 0]], 24)
    L.tube([[s * 1.6, 0, 1.0], [s * 1.6, 0, 27.6]], 0.52, 14)
  }
  L.role('metal').tube([[-3.6, 0, 28.4], [3.6, 0, 28.4]], 0.75, 18)
  for (const s of [-1, 1]) crossHandle(L, [s * 3.6, 0, 28.9], 1.0)
  gooseneck(L, [0, 0, 29.0], 9.1, 9.8, 31.6, 0.5)
  // the cradle and the telephone handset on top of the mixer, its hose looping down
  L.tube([[0, -0.4, 29.0], [0, -1.4, 31.4]], 0.3, 10)
  handset(L, [-3.6, -1.6, 31.9], [1, 0, 0], 7.0)
  L.role('metal').tube(LB.bez([-3.6, -1.6, 31.6], [-6, -2.5, 24], [-4, -2.6, 10], [-1.6, -0.6, 22], 20), 0.28, 8, false)
}
function fillerFloorColumn(L: LB) {
  L.role('metal').latheAt([0, 0, 0], [[0, 0.9], [1.2, 0.9], [1.6, 0.25], [1.6, 0], [0, 0]], 28)
  L.tube([[0, 0, 0.9], [0, 0, 33.8]], 0.82, 18)
  L.tube(LB.bez([0, 0, 33.6], [0, 0, 37.0], [0, 6.0, 37.2], [0, 11.8, 33.6], 22), 0.48, 14)
  L.cyl([0, 11.8, 33.8], [0, 11.8, 32.8], 0.5, 14)
  leverHandle(L, [0.8, 0, 30.0], 0, 2.6)
  // the slim hand shower docked along the column
  L.tube([[-0.95, -0.6, 22], [-0.95, -0.6, 29.6]], 0.45, 12)
  L.latheAt([-0.95, -0.6, 29.6], [[0, 1.6], [0.5, 1.6], [0.62, 0.6], [0, 0]], 16)
}
function fillerWallExposed(L: LB) {
  const z = 30
  for (const s of [-1, 1]) {
    wallRosette(L, [s * 4, 0, z], 1.15, 0.55)
    L.role('metal').tube([[s * 4, 0.5, z], [s * 4, 2.8, z]], 0.5, 14)
    crossHandle(L, [s * 4, 2.8, z + 0.6], 1.0)
  }
  L.role('metal').tube([[-5.0, 2.8, z], [5.0, 2.8, z]], 0.72, 18)
  L.tube(LB.bez([0, 2.8, z - 0.4], [0, 2.8, z - 2.0], [0, 4.2, z - 2.4], [0, 7.6, z - 2.4], 14), 0.55, 14)
  L.cyl([0, 7.6, z - 2.1], [0, 7.6, z - 3.1], 0.6, 14)
  L.tube([[0, 2.8, z + 0.6], [0, 2.8, z + 2.6]], 0.3, 10)
  handset(L, [-3.4, 3.0, z + 3.0], [1, 0, 0], 6.8)
}
function fillerRimTelephone(L: LB, R: number) {
  for (const s of [-1, 1]) {
    rosette(L, [s * 3.5, 0, R], 0.9, 0.4)
    L.role('metal').latheAt([s * 3.5, 0, R + 0.4], [[0, 3.0], [0.5, 3.0], [0.5, 0], [0, 0]], 16)
    crossHandle(L, [s * 3.5, 0, R + 4.0], 0.9)
  }
  L.role('metal').tube([[-3.5, 0, R + 3.3], [3.5, 0, R + 3.3]], 0.62, 16)
  L.tube(LB.bez([0, 0, R + 3.0], [0, 0, R + 2.0], [0, 2.6, R + 2.0], [0, 4.6, R + 1.6], 12), 0.46, 14)
  L.tube([[0, 0, R + 3.8], [0, 0, R + 6.2]], 0.3, 10)
  L.tube([[-1.6, 0, R + 6.4], [1.6, 0, R + 6.4]], 0.24, 8)
  handset(L, [-3.6, 0, R + 7.1], [1, 0, 0], 7.0)
}

// ======================================================================================
// BATHTUBS — built in (y = 0 the back wall, floor z = 0)
// ======================================================================================

function tubAlcove(L: LB, W: number) {
  const a = W / 2, d = 32, b = d / 2
  const o = (z: number, k = 1) => ring(sel(a * k, b * k, 14, 0, b), z)
  const i = (k: number, z: number, cy = b + 0.8) => ring(sel((a - 3.2) * k, (b - 3.6) * k, 4, 0, cy), z)
  L.role('body').loft([o(0), o(14.8), o(15.5, 0.998)], [1])
  L.capLoop(o(0), false)
  L.bridge(o(15.5, 0.998), i(1, 15.5))
  L.role('enamel').loft([i(1, 15.5), i(0.99, 13), i(0.94, 6), i(0.86, 2.4), i(0.82, 2.0)])
  L.capLoop(i(0.82, 2.0), true)
  drain(L, [-a + 8, b + 0.8, 2.01], 0.9)
  L.role('metal').latheAt([-a + 3.25, b + 0.8, 11.5], [[0, 0.3], [1.4, 0.2], [1.5, 0], [0, 0]], 20, 0, ALONG_X)   // overflow plate
}
function tubDropIn(L: LB, W: number) {
  const a = W / 2, d = 34, b = d / 2
  const o = (z: number, k = 1) => ring(sel(a * k, b * k, 10, 0, b), z)
  const i = (k: number, z: number) => ring(sel((a - 3) * k, (b - 3.2) * k, 3.4, 0, b), z)
  L.role('body').loft([ring(sel(a - 4, b - 3.6, 4, 0, b), 0), o(20.6, 0.97), o(20.6), o(22)], [2])
  L.capLoop(ring(sel(a - 4, b - 3.6, 4, 0, b), 0), false)
  L.bridge(o(22), i(1, 22))
  L.role('enamel').loft([i(1, 22), i(0.98, 18), i(0.9, 8), i(0.78, 3.6), i(0.74, 3.2)])
  L.capLoop(i(0.74, 3.2), true)
  drain(L, [-a + 9, b, 3.21], 0.9)
}

// ======================================================================================
// BATHTUBS — freestanding (centred at (0, d/2), floor z = 0)
// ======================================================================================

/** the tub's rim height along its length (slipper ends rise) */
type RimZ = (x: number) => number
/** a freestanding shell: the outside from `baseZ` up to the rim, the rim (rolled or flat), the
 *  inside down to the floor of the bath */
function freeShell(L: LB, W: number, D: number, o: {
  baseZ: number; baseK: number; rimZ: RimZ; e?: number; rolled?: boolean; rimW?: number; floorZ: number; skirt?: boolean; taper?: number; cy?: number
}) {
  const a = W / 2, b = D / 2, cy = o.cy ?? b, e = o.e ?? 2.6
  const outline = (k: number, kb = k) => sel(a * k, b * kb, e, 0, cy)
  const lerpRing = (pts: P2[], t: number, zBot: number) => ringZ(pts, (p) => zBot + (o.rimZ(p[0]) - zBot) * t)
  const top = outline(o.rolled ? 0.965 : 1)
  const levels = o.skirt
    ? [[outline(o.baseK), 0], [outline(o.baseK * 1.005), 0.06], [outline(0.97), 0.55], [outline(0.995), 0.88], [top, 1]]
    : [[outline(o.baseK, o.baseK * (o.taper ?? 1)), 0], [outline(o.baseK + (1 - o.baseK) * 0.45), 0.25], [outline(0.98), 0.7], [top, 1]]
  const rings = (levels as [P2[], number][]).map(([pts, t]) => lerpRing(pts, t, o.baseZ))
  L.role('body').loft(rings, o.skirt ? [1] : [])
  L.capLoop(rings[0], false)
  const rimLoop = ringZ(top, (p) => o.rimZ(p[0]))
  const rimW = o.rimW ?? 1.4
  const inPts = sel(a - rimW - (o.rolled ? 1.1 : 0), b - rimW - (o.rolled ? 1.1 : 0), e, 0, cy)
  const innerTop = ringZ(inPts, (p) => o.rimZ(p[0]) - (o.rolled ? 0.6 : 0))
  if (o.rolled) {
    // the rolled rim: a round bead along the top, in the white enamel
    L.role('enamel').loopTube(ringZ(outline(0.985), (p) => o.rimZ(p[0]) + 0.1), 1.1, 12)
    L.bridge(ringZ(outline(0.965), (p) => o.rimZ(p[0]) + 0.6), innerTop)
  } else {
    L.role('body').bridge(rimLoop, innerTop)
  }
  const fl = (k: number, z: number) => ring(sel((a - rimW - 1) * k, (b - rimW - 1) * k, Math.min(e, 2.6), 0, cy), z)
  const inner = [innerTop, ringZ(inPts.map((p) => [p[0] * 0.99, cy + (p[1] - cy) * 0.99] as P2), (p) => o.rimZ(p[0]) - 3.5),
    fl(0.86, o.floorZ + 3.5), fl(0.72, o.floorZ + 0.6), fl(0.68, o.floorZ)]
  L.role(o.rolled ? 'enamel' : 'body').loft(inner)
  L.capLoop(fl(0.68, o.floorZ), true)
  drain(L, [-(a - rimW) * 0.62, cy, o.floorZ + 0.01], 0.9)
}
/** a ball-and-claw foot under the shell at (x, y), reaching up to z */
function clawFoot(L: LB, x: number, y: number, z: number, out: number) {
  L.role('metal').sphere([x, y, 1.25], 1.25, 14)
  for (const da of [-0.55, 0, 0.55]) {
    const a = out + da
    L.tube(LB.bez([x + Math.cos(a) * 0.4, y + Math.sin(a) * 0.4, 2.6], [x + Math.cos(a) * 1.6, y + Math.sin(a) * 1.6, 2.4], [x + Math.cos(a) * 1.5, y + Math.sin(a) * 1.5, 0.6], [x + Math.cos(a) * 1.05, y + Math.sin(a) * 1.05, 0.25], 8), 0.26, 6)
  }
  L.latheAt([x, y, 2.2], [[0, z - 2.2], [1.6, z - 2.2], [1.3, z - 3.4], [0.82, z - 5.2], [0.95, 1.2], [1.2, 0.4], [0, 0]], 16)
}
function tubClawfoot(L: LB, W: number, D: number, slipper: 'none' | 'double') {
  const rim: RimZ = slipper === 'double' ? (x) => 22.8 + 6.1 * Math.abs(x / (W / 2 - 0.65)) ** 3 : () => 24.7
  freeShell(L, W - 1.3, D - 1.9, { baseZ: 6.2, baseK: 0.8, rimZ: rim, e: 2.5, rolled: true, rimW: 1.0, floorZ: 8.6, cy: D / 2 })
  const fx = W / 2 - 10.5, fy = D / 2 - 6.2
  for (const sx of [-1, 1]) for (const sy of [-1, 1]) clawFoot(L, sx * fx, D / 2 + sy * fy, 7.0, Math.atan2(sy * 0.6, sx))
}
function tubBateau(L: LB, W: number, D: number) {
  freeShell(L, W, D, { baseZ: 0, baseK: 0.9, rimZ: (x) => 24.2 + 3.8 * Math.abs(x / (W / 2)) ** 2.4, e: 2.5, rimW: 1.2, floorZ: 4.0, skirt: true })
}
function tubOvalStone(L: LB, W: number, D: number) {
  freeShell(L, W, D, { baseZ: 0, baseK: 0.9, rimZ: () => 24, e: 2.8, rimW: 1.5, floorZ: 3.6, skirt: true })
}
function tubOvoid(L: LB, W: number, D: number) {
  freeShell(L, W, D, { baseZ: 0, baseK: 0.6, rimZ: () => 21, e: 2.3, rimW: 1.1, floorZ: 4.0, taper: 1.0 })
}
function tubStoneBlock(L: LB, W: number, D: number) {
  const a = W / 2, b = D / 2
  const o = (z: number, k = 1) => ring(sel(a * k, b * k, 12, 0, b), z)
  const i = (k: number, z: number) => ring(sel((a - 4) * k, (b - 3.5) * k, 2.6, 0, b), z)
  L.role('body').loft([o(0), o(21.6), o(22, 0.996)], [1])
  L.capLoop(o(0), false)
  L.bridge(o(22, 0.996), i(1, 22))
  L.loft([i(1, 22), i(0.98, 18), i(0.86, 7), i(0.72, 4.3), i(0.7, 4)])
  L.capLoop(i(0.7, 4), true)
  drain(L, [-(a - 4) * 0.6, b, 4.01], 0.9)
}

// ======================================================================================
// SHOWER VALVES (wall y = 0, the valve centre at z = M)
// ======================================================================================

function valveConcealed(L: LB, M: number, square: boolean) {
  if (square) {
    L.role('metal').box(-3.5, 3.5, 0, 0.32, M - 3.5, M + 3.5)
    L.box(-3.3, 3.3, 0.32, 0.4, M - 3.3, M + 3.3)
    L.latheAt([0, 0.4, M], [[0, 1.3], [0.9, 1.3], [0.9, 0], [0, 0]], 4, Math.PI / 4, FACE_OUT)
    L.tube([[0, 1.5, M], [3.2, 1.6, M]], 0.2, 8)
  } else {
    L.role('metal').latheAt([0, 0, M], [[0, 0.45], [3.3, 0.45], [3.6, 0.3], [3.75, 0.1], [3.75, 0], [0, 0]], 40, 0, FACE_OUT)
    L.latheAt([0, 0.45, M], [[0, 1.6], [0.7, 1.6], [0.8, 1.2], [1.1, 0.4], [1.2, 0], [0, 0]], 24, 0, FACE_OUT)
    L.tube([[0, 1.8, M], [1.4, 1.9, M + 0.15], [3.4, 1.9, M + 0.5]], 0.22, 10)
    L.sphere([3.4, 1.9, M + 0.5], 0.28, 10)
  }
}
function valveThermoCross(L: LB, M: number) {
  L.role('metal').latheAt([0, 0, M], [[0, 0.45], [3.3, 0.45], [3.6, 0.3], [3.75, 0.1], [3.75, 0], [0, 0]], 40, 0, FACE_OUT)
  L.frame([0, 0.45, M], FACE_OUT, () => crossHandle(L, [0, 0, 0], 1.25))
  L.role('metal').latheAt([0, 0, M - 8], [[0, 0.4], [2.2, 0.4], [2.45, 0.1], [2.45, 0], [0, 0]], 32, 0, FACE_OUT)
  L.frame([0, 0.4, M - 8], FACE_OUT, () => crossHandle(L, [0, 0, 0], 0.95))
}
/** the exposed thermostatic bar at height z (also the riser system's base) */
function exposedBar(L: LB, z: number) {
  for (const s of [-1, 1]) {
    wallRosette(L, [s * 3.94, 0, z], 1.2, 0.55)
    L.role('metal').tube([[s * 3.94, 0.5, z], [s * 3.94, 3.2, z]], 0.55, 14)
    L.latheAt([s * 3.94, 2.4, z], [[0, 0.9], [0.8, 0.9], [0.8, 0], [0, 0]], 6, Math.PI / 6, FACE_OUT)
  }
  L.role('metal').tube([[-5.2, 3.2, z], [5.2, 3.2, z]], 1.88, 24)
  for (const s of [-1, 1]) L.frame([s * 5.2, 3.2, z], s > 0 ? ALONG_X : rotY(-Math.PI / 2), () => crossHandle(L, [0, 0, 0], 1.0))
  L.latheAt([0, 3.2, z + 1.8], [[0, 1.0], [0.62, 1.0], [0.75, 0.4], [0.75, 0], [0, 0]], 18)
}

// ======================================================================================
// SHOWERHEADS (wall y = 0, the arm at z = M) and HAND SHOWERS (z = M the bar's foot)
// ======================================================================================

/** a round head / rose whose top centre is at c: `dome` adds the traditional rose's crown */
function roseHead(L: LB, c: V3, r: number, dome: boolean) {
  const prof: [number, number][] = dome
    ? [[0, 1.5], [0.55, 1.5], [0.7, 0.9], [r * 0.7, 0.2], [r, -0.6], [r, -1.0], [0, -1.0]]
    : [[0, 0.0], [0.9, 0.0], [r - 0.3, -0.45], [r, -0.8], [r, -1.1], [0, -1.1]]
  L.role('metal').latheAt(c, prof, 40)
  L.role('dark').latheAt(add(c, [0, 0, (dome ? -1.0 : -1.1) - 0.01]), [[0, 0.01], [r * 0.9, 0.01], [r * 0.9, 0], [0, 0]], 32)
}
function headRainArm(L: LB, M: number) {
  wallRosette(L, [0, 0, M], 1.25, 0.6)
  L.role('metal').tube([[0, 0.5, M], [0, 10.2, M], [0, 11, M - 0.6], [0, 11, M - 1.2]], 0.42, 14)
  roseHead(L, [0, 11, M - 1.4], 5.0, false)
}
function headRoseGooseneck(L: LB, M: number) {
  wallRosette(L, [0, 0, M], 1.25, 0.6)
  L.role('metal').tube(LB.bez([0, 0.5, M], [0, 5.5, M], [0, 9.5, M + 5.2], [0, 10, M - 2.4], 20), 0.42, 14)
  roseHead(L, [0, 10, M - 2.6], 4.0, true)
}
function headCeilingRose(L: LB, M: number) {
  const cz = M + 14
  L.role('metal').latheAt([0, 10, cz], [[0, 0], [1.3, 0], [1.3, -0.35], [0.7, -0.7], [0, -0.7]], 28)
  L.tube([[0, 10, cz - 0.5], [0, 10, M + 1.4]], 0.42, 14)
  roseHead(L, [0, 10, M + 1.2], 4.0, true)
}
function headRiser(L: LB, M: number) {
  const zb = M - 40
  exposedBar(L, zb)
  L.role('metal').tube([[0, 3.2, zb + 2.6], [0, 3.2, M - 0.4], [0, 3.6, M + 0.5], [0, 5, M + 0.9], ...LB.bez([0, 5, M + 0.9], [0, 9, M + 1.4], [0, 11.6, M + 0.6], [0, 12, M - 1.4], 12).slice(1)], 0.5, 14)
  // the wall clip steadying the riser
  wallRosette(L, [0, 0, M - 16], 0.9, 0.45)
  L.role('metal').tube([[0, 0.4, M - 16], [0, 2.7, M - 16]], 0.3, 10)
  L.latheAt([0, 3.2, M - 16.7], [[0, 1.4], [0.85, 1.4], [0.85, 0], [0, 0]], 18)
  roseHead(L, [0, 12, M - 1.6], 4.0, true)
}
function handSlideBar(L: LB, M: number) {
  for (const z of [M, M + 30]) {
    wallRosette(L, [0, 0, z], 1.0, 0.5)
    L.role('metal').tube([[0, 0.45, z], [0, 1.9, z]], 0.42, 12)
  }
  L.role('metal').tube([[0, 1.9, M - 0.4], [0, 1.9, M + 30.4]], 0.45, 14)
  L.latheAt([0, 1.9, M + 19.2], [[0, 1.8], [0.85, 1.8], [0.85, 0], [0, 0]], 18)
  // the handset clipped to the slider, its hose looping down to the wall elbow
  L.frame([0, 3.0, M + 15], rotX(-0.38), () => handset(L, [0, 0, 0], [0, 0, 1], 6.8))
  L.role('metal').tube(LB.bez([0, 2.9, M + 14.6], [0, 3.6, M + 2], [2.6, 3.2, M - 6.5], [2.6, 0.8, M - 4.2], 20), 0.3, 8, false)
  wallRosette(L, [2.6, 0, M - 4.2], 0.85, 0.45)
  L.role('metal').tube([[2.6, 0.4, M - 4.2], [2.6, 1.1, M - 4.2]], 0.35, 10)
}
function handTelephoneHook(L: LB, M: number) {
  wallRosette(L, [0, 0, M + 6], 1.0, 0.5)
  L.role('metal').tube([[0, 0.45, M + 6], [0, 2.6, M + 6], [0, 3.0, M + 6.6]], 0.3, 10)
  L.tube([[-1.4, 2.6, M + 6.2], [1.4, 2.6, M + 6.2]], 0.22, 8)
  handset(L, [-3.6, 2.8, M + 7.0], [1, 0, 0], 7.0)
  L.role('metal').tube(LB.bez([-3.6, 2.8, M + 6.8], [-6.0, 3.2, M - 1], [-1.8, 3.0, M - 7], [0, 0.8, M - 4.6], 20), 0.3, 8, false)
  wallRosette(L, [0, 0, M - 4.6], 0.85, 0.45)
}

// ======================================================================================
// WALL-HUNG TOILET (2026-10-21) — the rimless D-bowl on its in-wall carrier. Every number comes
// from the kernel's WALL_HUNG table (the maker's US drawings); y = 0 the FINISHED wall face, the
// carrier behind it, z = 0 the floor, R the rim (the top of the ceramic).
// ======================================================================================

/** resample a closed polyline to n points by arc length, starting at its first point */
function resampleLoop(pts: P2[], n: number): P2[] {
  const seg: number[] = []
  let total = 0
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length]
    const l = Math.hypot(b[0] - a[0], b[1] - a[1])
    seg.push(l); total += l
  }
  const out: P2[] = []
  let i = 0, acc = 0
  for (let k = 0; k < n; k++) {
    const want = (total * k) / n
    while (acc + seg[i] < want && i < seg.length - 1) { acc += seg[i]; i++ }
    const a = pts[i], b = pts[(i + 1) % pts.length]
    const t = seg[i] > 1e-9 ? (want - acc) / seg[i] : 0
    out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])
  }
  return out
}

const DN = 72
/**
 * The D-plan of the bowl (and its lid): a straight back at y0 with eased corners `rc`, straight
 * sides out to `ys`, then the long front curve (a half superellipse) to the front at y = `d`.
 * CCW seen from above, starting at the back centre, so every ring of a loft lines up. Shared
 * with the plan symbol, so the drawing and the model are the same outline.
 */
export function dPlan(hw: number, d: number, ys: number, y0 = 0, rc = 0.4, n = DN): P2[] {
  const p: P2[] = [[0, y0], [hw - rc, y0]]
  const arc = (cx: number, cy: number, r: number, a0: number, a1: number) => {
    for (let k = 1; k <= 6; k++) { const a = a0 + ((a1 - a0) * k) / 6; p.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]) }
  }
  arc(hw - rc, y0 + rc, rc, -Math.PI / 2, 0)
  const b = d - ys, e = 2.3
  for (let k = 0; k <= 40; k++) {
    const t = (Math.PI * k) / 40, c = Math.cos(t), s = Math.sin(t)
    p.push([hw * Math.sign(c) * Math.abs(c) ** (2 / e), ys + b * Math.abs(s) ** (2 / e)])
  }
  arc(-hw + rc, y0 + rc, rc, Math.PI, Math.PI * 1.5)
  // drop repeats so the arc-length walk never sees a zero segment
  const clean = p.filter((q, i) => i === 0 || Math.hypot(q[0] - p[i - 1][0], q[1] - p[i - 1][1]) > 1e-6)
  return resampleLoop(clean, n)
}
/** the bowl's throat: an oval narrowing toward the front, CCW from the back centre */
export function bowlThroat(a: number, b: number, cy: number, taper = 0.12, n = DN): P2[] {
  const out: P2[] = []
  for (let k = 0; k < n; k++) {
    const t = -Math.PI / 2 + (Math.PI * 2 * k) / n, c = Math.cos(t), s = Math.sin(t)
    const kk = 1 - taper * Math.max(0, s)
    out.push([a * kk * Math.sign(c) * Math.abs(c) ** (2 / 2.4), cy + b * Math.sign(s) * Math.abs(s) ** (2 / 2.4)])
  }
  return out
}
/** the throat at the rim (half-width, half-length, centre) — the plan symbol draws it too */
export const WALL_HUNG_THROAT = { a: 5.35, b: 7.2, cy: 13.4 } as const

export function wallHungRimless(L: LB, rimIn: number) {
  const H = WALL_HUNG, R = rimIn
  const r = wallHungRoughIn(R)
  const hw = H.bowlW / 2
  // ---- the ceramic: a flat top, sides near-plumb, the front leaning back as it drops ----
  const body: [number, number, number][] = [   // [depth below the rim, half-width, front]
    [0.0, hw, H.bowlD],
    [2.0, hw, H.bowlD - 0.05],
    [5.0, hw - 0.06, H.bowlD - 0.4],
    [8.0, hw - 0.2, H.bowlD - 1.2],
    [10.6, hw - 0.42, H.bowlD - 2.3],
    [H.skirtH, hw - 0.58, H.bowlD - 3.0],
  ]
  const rings = body.map(([dz, a, d]) => ring(dPlan(a, d, H.sideRunD * (d / H.bowlD)), R - dz))
  L.role('body')
  L.loft([...rings].reverse())
  // the rim top, the deck behind the seat included, round the throat
  const T = WALL_HUNG_THROAT
  const top = bowlThroat(T.a, T.b, T.cy)
  L.bridge(rings[0], ring(top, R))
  // the bowl's inside: rimless, a clean plumb lip, the washdown slope to the water, and below
  // the water the trap's dip into the bulb (the cut a section shows)
  const inner = (a: number, b: number, cy: number, dz: number) => ring(bowlThroat(a, b, cy), R - dz)
  const WATER = 9.8
  L.loft([ring(top, R), inner(T.a - 0.1, T.b - 0.15, T.cy, 0.6), inner(4.6, 6.0, 12.4, 3.0), inner(3.4, 4.2, 11.0, 6.0), inner(2.6, 3.1, 9.7, 8.6),
    inner(2.3, 2.7, 9.2, WATER), inner(2.0, 2.4, 9.0, 11.6), inner(1.5, 1.8, 9.0, 12.9), inner(0.6, 0.8, 9.0, 13.4)])
  L.role('water').capLoop(inner(2.3, 2.7, 9.2, WATER), true)
  // the skirt's underside, an open ring the trap's bulb drops through, rounded at its bottom
  const bulb = (k: number, dz: number) => ring(bowlThroat(2.8 * k, 3.3 * k, 9.0, 0), R - dz)
  L.role('body').bridge(rings[rings.length - 1], bulb(1, H.skirtH), true)
  L.loft([bulb(0.3, H.bodyH), bulb(0.6, H.bodyH - 0.1), bulb(0.8, H.bodyH - 0.32), bulb(0.93, H.bodyH - 0.7), bulb(0.99, H.bodyH - 1.2), bulb(1, H.skirtH)])
  L.capLoop(bulb(0.3, H.bodyH), false)
  // ---- the seat and the slim lid, flush with the ceramic from the deck forward ----
  const lid = (inset: number, z: number) => ring(dPlan(hw - inset, H.bowlD - inset, H.sideRunD, H.seatBackY + inset, 1.2), z)
  L.role('seat')
  L.loft([lid(0.2, R), lid(0.1, R + H.seatT)])
  L.loft([lid(0, R + H.seatT), lid(0, R + H.seatT + H.lidT)])
  L.capLoop(lid(0, R + H.seatT), false)
  L.capLoop(lid(0, R + H.seatT + H.lidT), true)
  // ---- the dual-flush plate on the finished wall, centred over the bowl ----
  const plate = (a: number, b: number, cx: number, cz: number): [number, number][] => sel(a, b, 9, cx, cz, 36)
  L.role('metal').plateY(plate(H.plateW / 2, H.plateH / 2, 0, r.plateZ), 0, H.plateT)
  // the two buttons, the reduced flush on the left, standing just proud of the plate
  L.plateY(plate(1.55, 2.25, -1.85, r.plateZ), H.plateT, H.plateT + 0.12)
  L.plateY(plate(1.95, 2.25, 1.45, r.plateZ), H.plateT, H.plateT + 0.12)
  // ---- the carrier in the stud bay (seen in sections, cutaways, and with the wall hidden) ----
  const yF = H.frameFrontY, yB = yF - H.frameD, fw = H.frameW / 2
  L.role('carrier')
  for (const s of [-1, 1]) {
    L.box(s < 0 ? -fw : fw - 1.6, s < 0 ? -fw + 1.6 : fw, yB, yF, r.frameZ0 + 3.8, r.frameZ1)   // side uprights
    L.box(s * 6.6 - 0.45, s * 6.6 + 0.45, yB + 0.6, yF - 0.4, 0, r.tankZ0)                       // the bowl's posts
    L.box(s * 6.6 - 1.25, s * 6.6 + 1.25, yB, yF, 0, 0.25)                                         // feet on the floor
  }
  L.box(-fw, fw, yB, yF, r.frameZ1 - 0.9, r.frameZ1)                       // top rail
  L.box(-fw + 1.6, fw - 1.6, yB, yF, r.frameZ0 + 3.8, r.frameZ0 + 4.6)    // bottom rail
  L.box(-7.4, 7.4, yB + 0.4, yF, r.rodZ - 0.7, r.rodZ + 0.7)               // the rod bracket
  // the two M12 mounting rods, through the wall into the bowl's back
  L.role('metal')
  for (const s of [-1, 1]) L.cyl([s * H.rodSpacing / 2, yF, r.rodZ], [s * H.rodSpacing / 2, 2.2, r.rodZ], 0.24, 16)
  // the concealed tank, its service opening behind the plate
  L.role('tank').box(-fw + 1.7, fw - 1.7, yB + 0.3, yF - 0.15, r.tankZ0, r.tankZ1)
  L.box(-4.0, 4.0, yF - 0.15, -0.02, r.plateZ - 2.6, r.plateZ + 2.6)
  // the flush pipe from the tank's bottom down to the bowl's upper spigot
  const fy = (yF + yB) / 2
  L.role('pipe').tube([[0, fy, r.tankZ0], ...LB.bez([0, fy, r.inletZ + 3], [0, fy, r.inletZ + 0.6], [0, yF, r.inletZ], [0, 1.2, r.inletZ], 10)], H.flushPipeOD / 2, 18)
  // the waste: the bowl's horizontal outlet into the carrier bend, dropping into the floor
  L.tube([[0, 1.2, r.outletZ], ...LB.bez([0, yF, r.outletZ], [0, r.wasteY + 0.6, r.outletZ], [0, r.wasteY, r.outletZ - 0.6], [0, r.wasteY, r.outletZ - H.wasteOD], 10), [0, r.wasteY, 0]], H.wasteOD / 2, 18)
}

// ======================================================================================
// the proto
// ======================================================================================

const ROLE_HEX: Record<string, string> = {
  enamel: '#f4f2ed', seat: '#f6f5f1', porcelain: '#f4f1ea', wood: '#7a5a3a', water: '#c3d1d1', dark: '#33312e',
  // OUTDOOR GRILLS
  grate: '#2a2826', char: '#1f1d1b', rubber: '#1d1d1d', knob: '#1c1c1c',
  // WALL-HUNG TOILET: the powder-coated carrier, its grey tank, the black HDPE pipe
  carrier: '#3d4245', tank: '#b9bcbd', pipe: '#2b2b2b',
}
const CHINA_ROLES = new Set(['enamel', 'seat', 'porcelain', 'water'])

export interface PlumbingBuildOpts { wIn: number; R: number; M: number; /** a freestanding tub's built-in floor filler */ filler?: boolean }

/** build a style's mesh into a fresh kit (exported for the extents test and the viewer) */
export function buildPlumbingKit(kind: string, st: PlumbingStyle, o: PlumbingBuildOpts): LB {
  const L = new LB()
  const { wIn: W, R, M } = o
  switch (`${kind}:${st.value}`) {
    case 'toilet:closeCoupled': toiletCloseCoupled(L); break
    case 'toilet:highTank': toiletHighTank(L); break
    case 'toilet:skirted': toiletSkirted(L); break
    case 'toilet:wallHung': toiletWallHung(L); break
    case 'wallHungToilet:rimlessD': wallHungRimless(L, R); break
    case 'bathSink:undermountRect': basinUndermountRect(L, R); break
    case 'bathSink:undermountOval': basinUndermountOval(L, R); break
    case 'bathSink:vesselRound': basinVesselRound(L, R); break
    case 'bathSink:vesselBlock': basinVesselBlock(L, R); break
    case 'bathSink:washstand': basinWashstand(L, W); break
    case 'bathSink:pedestal': basinPedestal(L); break
    case 'bathSink:trough': basinTrough(L, W); break
    case 'kitchenSink:apronFireclay': apronSink(L, W, R, {}); break
    case 'kitchenSink:apronFluted': apronSink(L, W, R, { fluted: true, d: 18.5 }); break
    case 'kitchenSink:apronStone': apronSink(L, W, R, { stone: true, d: 20 }); break
    case 'kitchenSink:undermountSteel': sinkUndermountSteel(L, W, R); break
    case 'kitchenSink:prepStone': sinkPrepStone(L, W, R); break
    case 'lavFaucet:crossWidespread': lavWidespread(L, R, 'cross'); break
    case 'lavFaucet:leverWidespread': lavWidespread(L, R, 'lever'); break
    case 'lavFaucet:porcelainLever': lavWidespread(L, R, 'porcelain'); break
    case 'lavFaucet:bridge': lavBridge(L, R); break
    case 'lavFaucet:gooseneckLevers': lavGooseneckLevers(L, R); break
    case 'lavFaucet:industrial': lavIndustrial(L, R); break
    case 'lavFaucet:minimal': lavMinimal(L, R); break
    case 'lavFaucet:tallVessel': lavMinimal(L, R, true); break
    case 'lavFaucet:wallCross': lavWall(L, R, true); break
    case 'lavFaucet:wallMinimal': lavWall(L, R, false); break
    case 'kitchenFaucet:bridgeSpray': kBridge(L, R, false, true); break
    case 'kitchenFaucet:bridgeLever': kBridge(L, R, true, false); break
    case 'kitchenFaucet:deckMixer': kDeckMixer(L, R); break
    case 'kitchenFaucet:wallBridge': kWallBridge(L, R); break
    case 'kitchenFaucet:gooseneck': kGooseneck(L, R); break
    case 'kitchenFaucet:preRinse': kPreRinse(L, R); break
    case 'potFiller:articulatedCross': potFiller(L, M, true); break
    case 'potFiller:articulatedMinimal': potFiller(L, M, false); break
    case 'tubFiller:floorTwin': fillerFloorTwin(L); break
    case 'tubFiller:floorColumn': fillerFloorColumn(L); break
    case 'tubFiller:wallExposed': fillerWallExposed(L); break
    case 'tubFiller:rimTelephone': fillerRimTelephone(L, R); break
    case 'tub:alcoveApron': tubAlcove(L, W); break
    case 'tub:dropIn': tubDropIn(L, W); break
    case 'tubFreestanding:clawfoot': tubClawfoot(L, W, st.d, 'none'); break
    case 'tubFreestanding:doubleSlipper': tubClawfoot(L, W, st.d, 'double'); break
    case 'tubFreestanding:bateau': tubBateau(L, W, st.d); break
    case 'tubFreestanding:ovalStone': tubOvalStone(L, W, st.d); break
    case 'tubFreestanding:ovoidTapered': tubOvoid(L, W, st.d); break
    case 'tubFreestanding:stoneBlock': tubStoneBlock(L, W, st.d); break
    case 'outdoorGrill:builtInGas': grillBuiltInGas(L, W, R); break
    case 'outdoorGrill:hybridCart': grillHybridCart(L, W); break
    case 'outdoorGrill:kamadoTable': grillKamadoTable(L, W); break
    case 'outdoorGrill:argentine': grillArgentine(L, W, R); break
    case 'outdoorGrill:santaMaria': grillSantaMaria(L, W); break
    case 'outdoorGrill:offsetSmoker': grillOffsetSmoker(L, W); break
    case 'showerValve:concealedLever': valveConcealed(L, M, false); break
    case 'showerValve:squarePlate': valveConcealed(L, M, true); break
    case 'showerValve:thermoCross': valveThermoCross(L, M); break
    case 'showerValve:exposedBar': exposedBar(L, M); break
    case 'showerHead:rainArm': headRainArm(L, M); break
    case 'showerHead:roseGooseneck': headRoseGooseneck(L, M); break
    case 'showerHead:ceilingRose': headCeilingRose(L, M); break
    case 'showerHead:riserSystem': headRiser(L, M); break
    case 'handShower:slideBar': handSlideBar(L, M); break
    case 'handShower:telephoneHook': handTelephoneHook(L, M); break
    default: break
  }
  // FREESTANDING TUB: the type's built-in floor filler (on by default since 2026-10-06) — the
  // single column past the drain end, its spout reaching over the rim
  if (kind === 'tubFreestanding' && o.filler) {
    const [fx, fy] = tubFillerPoint(W / 12, st.d / 12)
    L.frame([fx * 12, fy * 12, 0], rotZ(-Math.PI / 2), () => fillerFloorColumn(L))
  }
  return L
}

/** the counter plane / mount point a styled fixture's proto is built at (inches) */
export function plumbingBuildOpts(kind: string, st: PlumbingStyle, tp: Record<string, ParamValue>): PlumbingBuildOpts {
  const rimFt = (tp.rimHeightFt as number) || 3
  const mountFt = (tp.mountHeightFt as number) > 0 ? (tp.mountHeightFt as number) : (st.mountFt ?? WALL_MOUNT_DEFAULT_FT[kind] ?? 4)
  // WALL-HUNG TOILET: R is its own rim height (the carrier setting), not a counter
  if (kind === 'wallHungToilet') return { wIn: plumbingWidthIn(st, tp), R: wallHungRimIn(tp), M: 0 }
  return { wIn: plumbingWidthIn(st, tp), R: plumbingOnCounter(kind, st) ? rimFt * 12 : 0, M: mountFt * 12, filler: kind === 'tubFreestanding' && tp.tubFiller !== false }
}

/** the kinds whose catalog z is measured from their wall MOUNT point (the rest: the floor, or
 *  the counter for counter-sitting styles) */
const MOUNT_RELATIVE = new Set(['potFiller', 'showerValve', 'showerHead', 'handShower'])

/** a styled fixture's vertical extent relative to its instance base, in FEET — mirrors where
 *  buildPlumbingKit puts the body (counter at the rim height, wall kinds about the mount).
 *  `hosted` = sitting on a real counter / another object's top: the counter is the base. */
export function plumbingZSpanFt(kind: string, st: PlumbingStyle, tp: Record<string, ParamValue>, hosted: boolean): { z0: number; z1: number } {
  const o = plumbingBuildOpts(kind, st, tp)
  // WALL-HUNG TOILET: the carrier's feet on the floor to its top, raised with the rim
  if (kind === 'wallHungToilet') { const z = wallHungZSpanIn(o.R); return { z0: z.z0 / 12, z1: z.z1 / 12 } }
  const base = plumbingOnCounter(kind, st) ? (hosted ? 0 : o.R) : MOUNT_RELATIVE.has(kind) ? o.M : 0
  return { z0: (base + st.z0) / 12, z1: (base + st.z1) / 12 }
}

/** the hole a sink cuts in the countertop it sits in (local inches), or null: an undermount
 *  bowl's opening, an apron sink's whole outline (it replaces the counter there, through the
 *  front edge); a vessel sits on the stone and cuts nothing but its drain */
export function plumbingCounterCutIn(kind: string, st: PlumbingStyle, W: number): P2[] | null {
  switch (`${kind}:${st.value}`) {
    case 'bathSink:undermountRect': return sel(8.6, 6.5, 5.5, 0, 7.8)
    case 'bathSink:undermountOval': return sel(8.5, 7.0, 2.1, 0, 8.1)
    case 'kitchenSink:apronFireclay': case 'kitchenSink:apronFluted': case 'kitchenSink:apronStone':
      return sel(W / 2, st.d / 2, 12, 0, st.d / 2)
    case 'kitchenSink:undermountSteel': return sel(W / 2 - 0.6, 8.6, 9, 0, 9)
    case 'kitchenSink:prepStone': return sel(W / 2 / 1.1, 7, 6, 0, 7.5)
    // OUTDOOR GRILLS: a built-in head's liner / an Argentine firebox
    case 'outdoorGrill:builtInGas': return [[-W / 2 + 1.5, 1], [W / 2 - 1.5, 1], [W / 2 - 1.5, 22], [-W / 2 + 1.5, 22]]
    case 'outdoorGrill:argentine': return [[-W / 2 + 1, 2], [W / 2 - 1, 2], [W / 2 - 1, 24], [-W / 2 + 1, 24]]
    default: return null
  }
}

/** FAUCET SNAP (2026-10-07, Matt: "a 'snapping' function for faucets used next to basins so we
 *  can place them accurately and quickly"): where a faucet / filler sits on a basin or tub, in
 *  the BASIN's local frame (inches), and the turn it takes relative to the basin's own
 *  (degrees). Deck faucets centre on the bowl, their holes 2-1/4" behind its back edge; wall
 *  faucets go on the wall the basin backs to; a floor filler stands past a freestanding tub's
 *  drain end, a rim filler on its rim there, both facing along the tub. */
export function plumbingFaucetSpotIn(
  basinKind: string, basin: PlumbingStyle, W: number, faucetKind: string, faucet: PlumbingStyle,
): { x: number; y: number; turnDeg: number } | null {
  const wall = faucet.mount === 'wall'
  if ((faucetKind === 'lavFaucet' && basinKind === 'bathSink') || (faucetKind === 'kitchenFaucet' && basinKind === 'kitchenSink')) {
    if (wall) return { x: 0, y: 0, turnDeg: 0 }
    const backOf: Record<string, number> = { vesselRound: 0, vesselBlock: 0, washstand: 5.6, pedestal: 4.9, trough: 4.6 }
    let back = backOf[basin.value]
    if (back == null) {
      const cut = plumbingCounterCutIn(basinKind, basin, W)
      back = cut ? Math.min(...cut.map((p) => p[1])) : 0
    }
    return { x: 0, y: back - 2.25, turnDeg: 0 }
  }
  if (faucetKind === 'tubFiller' && basinKind === 'tubFreestanding') {
    if (faucet.mount === 'floor') {
      const [fx, fy] = tubFillerPoint(W / 12, basin.d / 12)
      return { x: fx * 12, y: fy * 12, turnDeg: -90 }
    }
    if (faucet.mount === 'rim') return { x: -W / 2 + 2.2, y: basin.d / 2, turnDeg: -90 }
  }
  if (faucetKind === 'tubFiller' && basinKind === 'tub' && faucet.mount === 'wall') {
    return { x: -W / 2 + 8, y: 0, turnDeg: 0 }
  }
  return null
}

/** does the style sit on a counter (its proto built with the counter at the rim height)? */
export function plumbingOnCounter(kind: string, st: PlumbingStyle): boolean {
  if (st.counter === 'undermount' || st.counter === 'apron' || st.counter === 'vessel') return true
  if (kind === 'lavFaucet' || kind === 'kitchenFaucet') return true
  return st.mount === 'rim'
}

export function buildPlumbingProto(el: ResolvedElement, key: string): OpeningProto {
  const tp = el.typeParams as Record<string, ParamValue>
  const kind = String(tp.objectKind || '')
  const st = plumbingStyleOf(kind, tp)!
  const L = buildPlumbingKit(kind, st, plumbingBuildOpts(kind, st, tp))
  const b = new MeshBuilder()
  const body = bodyFinishOf(st, tp)
  const metal = el.memberColor || st.finish
  const groups = L.finish(b, (role) => {
    if (role === 'metal') return tintMaterialId(metal, PLUMBING_METAL_MATERIAL_ID)
    if (role === 'body') return tintMaterialId(body.hex, plumbingBodyMaterialId(body.base))
    const hex = ROLE_HEX[role] || '#cccccc'
    return tintMaterialId(hex, CHINA_ROLES.has(role) ? plumbingBodyMaterialId('china') : 'default')
  })
  const solid = b.build()
  const glass = L.glass.vertCount ? L.glass.build() : null
  return { key, solid, glass, edges: featureEdges(solid), solidMaterialId: null, solidGroups: groups }
}

/** the kit's mesh extents in inches (for the catalog test) */
export function plumbingKitExtentsIn(L: LB): { min: V3; max: V3 } {
  const b = new MeshBuilder()
  L.finish(b, () => null)
  const min: V3 = [Infinity, Infinity, Infinity], max: V3 = [-Infinity, -Infinity, -Infinity]
  for (const arr of [b.pos, L.glass.pos]) for (let i = 0; i < arr.length; i += 3) for (let k = 0; k < 3; k++) {
    const v = arr[i + k] * 12
    if (v < min[k]) min[k] = v
    if (v > max[k]) max[k] = v
  }
  return { min, max }
}
